import type {
  InviteEligibilityApplicant,
  InviteEligibilityOutcome,
  InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';

/** Create invite or edit invite; both share the eligibility controller. */
export type InviteEligibilityFlow = 'create' | 'edit';

/** `unavailable`: the preflight request failed and no banner was shown (fail open). */
export type InviteEligibilityPreflightDecision = InviteEligibilityOutcome | 'unavailable';

/** `not_checked`: the flag was off or the form never had enough input for a preflight. */
export type InviteEligibilitySubmitPreflightDecision =
  InviteEligibilityPreflightDecision | 'not_checked';

export type InviteEligibilityFinalDecision = 'created' | 'blocked' | 'error';

/**
 * Preflight result without renter data. Emails, record ids and addresses
 * stay out of analytics and RUM.
 */
export type InviteEligibilityPreflightSummary = {
  decision: InviteEligibilityOutcome;
  renter_count: number;
  match_count: number;
  warning_count: number;
  block_count: number;
  match_sources: string;
};

const countOutcome = (
  applicants: InviteEligibilityApplicant[],
  outcome: InviteEligibilityOutcome,
): number => applicants.filter((applicant) => applicant.outcome === outcome).length;

export const summarizeInviteEligibilityPreflight = (
  data: InviteEligibilityResponseData,
): InviteEligibilityPreflightSummary => {
  const applicants = data.applicants ?? [];
  return {
    decision: data.outcome,
    renter_count: applicants.length,
    match_count: applicants.reduce((total, applicant) => total + (applicant.total_matches ?? 0), 0),
    warning_count: countOutcome(applicants, 'warning'),
    block_count: countOutcome(applicants, 'block'),
    match_sources: data.match_sources || 'none',
  };
};

type EligibilityQueryState = { status: string; data?: InviteEligibilityResponseData } | undefined;

/** What the landlord saw for these exact inputs when they pressed submit. */
export const inviteEligibilitySubmitPreflightDecision = (
  state: EligibilityQueryState,
): InviteEligibilitySubmitPreflightDecision => {
  if (state?.status === 'error') {
    return 'unavailable';
  }
  return state?.data?.outcome ?? 'not_checked';
};

export const inviteEligibilityFinalDecision = (
  succeeded: boolean,
  eligibilityBlocked: boolean,
): InviteEligibilityFinalDecision => {
  if (succeeded) {
    return 'created';
  }
  return eligibilityBlocked ? 'blocked' : 'error';
};
